/**
 * Razorpay's hosted Checkout (the official popup). Loaded on demand, only on
 * the checkout page, and only once per page load.
 *
 * There is deliberately no mock here: a payment only counts once the backend
 * has verified Razorpay's signature AND re-fetched the payment from Razorpay
 * (see backend/app/payments.py). For a store using Razorpay TEST keys this
 * is Razorpay's own sandbox — the real popup, no real money.
 */
export const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/** "ios" / "android" inside the Capacitor app, null on the website. */
export function nativePlatform(win = typeof window !== "undefined" ? window : undefined) {
  const cap = win?.Capacitor;
  if (!cap?.isNativePlatform?.()) return null;
  return cap.getPlatform?.() || null;
}

/** True inside the Capacitor iOS / Android app. */
export function isNativeApp(win = typeof window !== "undefined" ? window : undefined) {
  return !!win?.Capacitor?.isNativePlatform?.();
}

/** True when the app shell has the native ExternalLinkPlugin (Android builds from iter121 on). */
export function hasUpiAppLinks(win = typeof window !== "undefined" ? window : undefined) {
  return !!win?.Capacitor?.isPluginAvailable?.("ExternalLink");
}

/**
 * Options for `new Razorpay(...)`.
 *
 * Inside a WebView Razorpay offers no UPI at all (no apps, no UPI ID) unless
 * `webview_intent: true` is sent; with it, it lists UPI apps (PhonePe, Google
 * Pay, Paytm…) and opens the chosen one with window.open().
 * - iOS: `phonepe://pay?…` links, which Capacitor and the shell's
 *   ExternalLinkPlugin hand to iOS.
 * - Android: app links or `intent://…#Intent;scheme=upi;package=…;end`, which
 *   only the shell's ExternalLinkPlugin can open — so Android sends it only
 *   when that plugin is present. Older Android builds stay as they were.
 */
export function checkoutOptions({ session, name, description, address, themeColor, onPaid, onDismiss,
  platform = nativePlatform(), upiAppLinks = hasUpiAppLinks() }) {
  return {
    key: session.key,
    order_id: session.razorpayOrderId,
    amount: session.amount,
    currency: session.currency,
    name,
    description,
    prefill: { name: address.name, contact: address.phone },
    theme: themeColor ? { color: themeColor } : undefined,
    // Razorpay closes the popup when this runs out, so nobody can pay for an
    // order after its reservation has expired.
    timeout: Math.max(60, Math.floor((session.expiresAt - Date.now()) / 1000)),
    handler: onPaid,
    modal: { ondismiss: onDismiss },
    ...(platform === "ios" || (platform === "android" && upiAppLinks) ? { webview_intent: true } : {}),
  };
}

let loading = null;

/** Resolves to `window.Razorpay`; rejects if the script cannot be loaded. */
export function loadRazorpay() {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve(window.Razorpay);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = RAZORPAY_CHECKOUT_SRC;
      script.async = true;
      script.onload = () => (window.Razorpay ? resolve(window.Razorpay) : reject(new Error("Razorpay unavailable")));
      script.onerror = () => reject(new Error("Could not load Razorpay"));
      document.body.appendChild(script);
    }).catch((e) => {
      loading = null; // let the next attempt try again (e.g. after going back online)
      throw e;
    });
  }
  return loading;
}
